import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import { Add as AddIcon, Close as CloseIcon } from '@material-ui/icons';

import { formatCurrency } from '../../../common/formatter';
import CardContent from '../../../common/card/CardContent';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import { WorkflowEntity } from '../../workflow/Workflow.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateSpor } from '../../supplier/Supplier.hooks';
import {
  BaseSporCardProps,
  OrderQuoteItem
} from '../../supplier/Supplier.interface';

function createInitialQuoteItem(): OrderQuoteItem {
  return { code: '', description: '', quantity: 0, price: 0 };
}

const StyledTable = styled(Table)`
  .label {
    font-weight: 500;
  }
  .price {
    margin-left: 20px;
  }
`;

const OrderQuoteTable: React.FC<BaseSporCardProps> = (
  props: BaseSporCardProps
) => {
  const { spor, onChange, onUpdate, onError } = props;
  const [quotes, setQuotes] = useState<OrderQuoteItem[]>(
    spor.quote?.[0] ? spor.quote : [createInitialQuoteItem()]
  );
  const [removeIndex, setRemoveIndex] = useState<number>(-1);
  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.Spor>(
    spor.task
  );
  const { updatedSpor, updateError, updateSpor } = useUpdateSpor(spor.id);
  const isQuoteDisabled: boolean = isFieldDisabled('quote');

  const totalPrice: number = useMemo(() => {
    return (quotes || []).reduce((total: number, quote: OrderQuoteItem) => {
      return total + (quote.price || 0) * (quote.quantity || 0);
    }, 0);
  }, [quotes]);

  const setAndUpdateQuotes = (newQuotes: OrderQuoteItem[]) => {
    setQuotes(newQuotes);
    updateSpor({ ...spor, quote: newQuotes });
  };

  const handleAdd = () => {
    setAndUpdateQuotes([...quotes, createInitialQuoteItem()]);
  };

  const handleUpdate = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
    number: boolean = false
  ) => {
    const { target } = event;
    const name: keyof OrderQuoteItem = target.name as keyof OrderQuoteItem;
    let value: string | number = target.value;
    // parse number
    if (number) {
      const parsed: number = parseFloat(`${value}`);
      value = isFinite(parsed) ? parsed : 0;
    }
    // skip if value did not change
    if (quotes[index][name] === value) {
      return;
    }
    const newQuotes: OrderQuoteItem[] = [...quotes];
    newQuotes[index] = { ...quotes[index], [name]: value };
    setAndUpdateQuotes(newQuotes);
  };

  const handleDelete = (index: number) => {
    const newQuotes: OrderQuoteItem[] = [...quotes];
    newQuotes.splice(index, 1);
    if (newQuotes.length === 0) {
      newQuotes.push(createInitialQuoteItem());
    }
    setAndUpdateQuotes(newQuotes);
  };

  useEffect(() => {
    setQuotes(spor.quote?.[0] ? spor.quote : [createInitialQuoteItem()]);
  }, [spor.quote]);

  useEffect(() => {
    if (updatedSpor) {
      onChange?.(updatedSpor);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedSpor]);

  useEffect(() => {
    if (updateError) {
      onError?.(updateError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateError]);

  return (
    <CardContent>
      <StyledTable>
        <TableHead>
          <TableRow>
            <TableCell>Item Code</TableCell>
            <TableCell>Item Description</TableCell>
            <TableCell>Quantity</TableCell>
            <TableCell>Unit Cost</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {quotes.map((quote: OrderQuoteItem, index: number) => (
            <TableRow key={`${quote.code}-${index}`}>
              <TableCell>
                <OutlinedInput
                  name='code'
                  placeholder='Code'
                  disabled={isQuoteDisabled}
                  defaultValue={quote.code}
                  onBlur={event => handleUpdate(event, index)}
                />
              </TableCell>
              <TableCell>
                <OutlinedInput
                  name='description'
                  placeholder='Description'
                  disabled={isQuoteDisabled}
                  defaultValue={quote.description}
                  onBlur={event => handleUpdate(event, index)}
                />
              </TableCell>
              <TableCell>
                <OutlinedInput
                  type='number'
                  name='quantity'
                  disabled={isQuoteDisabled}
                  defaultValue={quote.quantity}
                  onBlur={event => handleUpdate(event, index, true)}
                />
              </TableCell>
              <TableCell>
                <OutlinedInput
                  type='number'
                  name='price'
                  disabled={isQuoteDisabled}
                  defaultValue={quote.price}
                  onBlur={event => handleUpdate(event, index, true)}
                  startAdornment={
                    <InputAdornment position='start'>$</InputAdornment>
                  }
                />
              </TableCell>
              <TableCell>
                <IconButton
                  disabled={isQuoteDisabled}
                  onClick={() => setRemoveIndex(index)}
                >
                  <CloseIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={3}>
              <Typography className='label' align='right'>
                Total
              </Typography>
            </TableCell>
            <TableCell>
              <Typography className='label price'>
                {formatCurrency(totalPrice)}
              </Typography>
            </TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </StyledTable>

      <Box m={2} mb={0}>
        <Button
          variant='outlined'
          startIcon={<AddIcon />}
          disabled={isQuoteDisabled}
          onClick={handleAdd}
        >
          Add Item
        </Button>
      </Box>

      <ConfirmDialog
        open={removeIndex > -1}
        confirmLabel='Confirm'
        title='Remove Order Quote Item'
        message='Are you sure you want to remove this item?'
        onCancel={() => setRemoveIndex(-1)}
        onConfirm={() => {
          setRemoveIndex(-1);
          handleDelete(removeIndex);
        }}
      />
    </CardContent>
  );
};

export default OrderQuoteTable;
